/**
 * @file    CalculationsSettingsManager.tsx
 * @scope   VIEW
 * @summary Advanced-settings router for a field: picks the settings panel matching the field's
 *          calculation type (Percentage / NPS / TotalCount) and renders it.
 * @consumes PercentageSettingsManager, NpsSettingsManager, FieldsManager models
 * @produces `CalculationsSettingsManager` component + its Tab descriptor
 * @sideEffects None directly — edits bubble up via `onFieldChange`.
 * @notes Panel selection is a Strategy map (`CALC_TYPE_PANELS`): 2/4 → Percentage, 3 → NPS. Add a
 *        new calc-type panel here without touching the render logic.
 */
import React from 'react';
import { Tab } from 'components/TabNavigator/TabNavigator';
import { SelectedFieldModel } from 'models/Enterprise/FieldsManagerModel';
import PercentageSettings, { CalcSettingsPanelProps } from './PercentageSettingsManager';
import NpsSettings from './NpsSettingsManager';
import NumericSettings from './NumericSettingsManager';
import { usesNumericSettings } from '../../utils/views/partitionsManager/calculationsManager/numericSettingsUtils';
import './CalculationsSettingsManager.scss';

export const CalculationsSettingsManagerTab: Tab = {
  name: 'CalculationsSettingsManager',
  displayName: 'Advanced',
};

/** Maps calculation type identifiers to their settings panel component. */
const CALC_TYPE_PANELS: Record<string, React.FC<CalcSettingsPanelProps>> = {
  '2': PercentageSettings,
  '3': NpsSettings,
  '4': PercentageSettings,
};

/** Numeric fields (SimpleInt, SimpleDouble, IntegerQuestion, DoubleQuestion) use operator-based settings. */
const NUMERIC_CALC_TYPE_PANELS: Record<string, React.FC<CalcSettingsPanelProps>> = {
  '2': NumericSettings,
  '4': NumericSettings,
};

interface CalculationsSettingsManagerProps {
  onBack: () => void;
  selectedField: SelectedFieldModel | null;
  onFieldChange?: (field: SelectedFieldModel) => void;
}

const CalculationsSettingsManager: React.FC<CalculationsSettingsManagerProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBack: _onBack,
  selectedField,
  onFieldChange,
}) => {
  if (!selectedField) return null;

  const panels = usesNumericSettings(selectedField) ? NUMERIC_CALC_TYPE_PANELS : CALC_TYPE_PANELS;
  const Panel = panels[selectedField.calculationType?.type ?? ''] ?? null;

  return (
    <div className="manage-fields-content calculations-settings-manager">
      <div className="data-source-info calculations-settings-manager__header">
        <span className="dataSourceLabel dataSourcePartitionLabel">
          {selectedField.displayName ?? ''}
        </span>
        <span className="dataSourceFieldsLabel">
          {selectedField.calculationType?.displayName ?? ''}
        </span>
      </div>
      {Panel && (
        <div className="calculations-settings-manager__body">
          <Panel selectedField={selectedField} onFieldChange={onFieldChange} />
        </div>
      )}
    </div>
  );
};

export default CalculationsSettingsManager;
